import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import { auth, dbRealTime, db } from '../services/firebaseConfig';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, onValue, off, update, set, get, onDisconnect } from 'firebase/database';
import '../styles/PlaySession.css';
import YouTube from 'react-youtube';
import { QRCodeSVG } from 'qrcode.react';
import { config } from '../config/config';
import Cast from '../components/Cast';
import AdBreakModal from '../components/AdBreakModal';
import { useAdBreakFlow } from '../features/ads/hooks/useAdBreakFlow';
import {
  ADSTERRA_BANNER_SCRIPT_URL,
  ADSTERRA_BANNER_CONTAINER_ID,
} from '../features/ads/adsterra/adsterraConfig';

/**
 * Adsterra banner ad — injected once on mount for free users.
 * Renders as a normal block element in the page layout alongside
 * publisher content, so it is never a "behavioural screen".
 */
const AdsterraBanner = () => {
  useEffect(() => {
    // Avoid injecting the same script more than once
    if (document.getElementById('adsterra-banner-script')) return;
    const script = document.createElement('script');
    script.id = 'adsterra-banner-script';
    script.src = ADSTERRA_BANNER_SCRIPT_URL;
    script.async = true;
    script.setAttribute('data-cfasync', 'false');
    document.body.appendChild(script);
  }, []);

  return (
    <div className="inline-ad-banner">
      <p className="inline-ad-label">Advertisement</p>
      <div id={ADSTERRA_BANNER_CONTAINER_ID}></div>
      <p className="inline-ad-upgrade">
        ✨ <a href="/pricing">Upgrade</a> to remove ads
      </p>
    </div>
  );
};



const PlaySession = () => {
  const { sessionId } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [token, setToken] = useState(null);
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [queueItems, setQueueItems] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [player, setPlayer] = useState(null);
  const [unplayedQueueItems, setUnplayedQueueItems] = useState([]);
  const [isSessionOwner, setIsSessionOwner] = useState(false);
  const [addedSongs, setAddedSongs] = useState(new Set());
  const [showShareModal, setShowShareModal] = useState(false);
  const [isSessionShared, setIsSessionShared] = useState(false);
  const [videoLoadError, setVideoLoadError] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const castRef = useRef();
  const [isChangingSong, setIsChangingSong] = useState(false);
  const [participants, setParticipants] = useState({});
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [newJoinNotification, setNewJoinNotification] = useState(null);
  const hasJoinedRef = useRef(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [authMode, setAuthMode] = useState('guest'); // 'guest', 'signin', 'signup'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [toast, setToast] = useState(null); // For success messages

  const isFreeUser = !sessionDetails?.subscriptionPlan || sessionDetails.subscriptionPlan === 'free';

  // Ref so onResume can reference the latest continueToNextSong without stale closure
  const continueToNextSongRef = useRef(null);

  const { isAdBreakOpen, handleSongCompleted, handleContinueAdBreak, handleMaybeLater } =
    useAdBreakFlow({
      isPremiumUser: !isFreeUser,
      sessionId,
      userId: auth.currentUser?.uid,
      onResume: useCallback(() => continueToNextSongRef.current?.(), []),
    });

  const opts = {
    height: '400',
    width: '100%',
    playerVars: {
      autoplay: sessionDetails?.status === 'ACTIVE' ? 1 : 0,
      controls: !isCasting ? 1 : 0,
      enablejsapi: 1,
      playsinline: 1,
      rel: 0,
      origin: window.location.origin,
      widget_referrer: window.location.origin,
    },
  };

  const playerClassName = `video-player ${isCasting ? 'disabled-player' : ''}`;

  // eslint-disable-next-line no-unused-vars
  const handleCastClick = () => {
    try {
      if (!player) return;

      // Access the underlying iframe element
      const iframe = player.getIframe();
      if (!iframe) return;
      // Find the cast button within the iframe's document
      const castButton = iframe.contentDocument?.querySelector('.ytp-cast-button');
      
      if (castButton) {
        castButton.click();
      } else {
        // If we can't find the cast button, try to trigger it programmatically
        player.playVideo(); // Sometimes playing helps show the cast button
        
        // Wait a short moment for the cast button to appear
        setTimeout(() => {
          const retryButton = iframe.contentDocument?.querySelector('.ytp-cast-button');
          if (retryButton) {
            retryButton.click();
          } else {
            setError('Casting not available. Please check your devices.');
            setTimeout(() => setError(null), 3000);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error initiating cast:', error);
      setError('Unable to start casting. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const onPlayerReady = (event) => {
    console.log('Player ready event received');
    const ytPlayer = event.target;
    setPlayer(ytPlayer);
    
    // The YouTube component automatically loads the video from the videoId prop
    // We just need to handle autoplay if the session is active and playing
    if (isPlaying && isSessionOwner && sessionDetails?.status === 'ACTIVE') {
      setTimeout(() => {
        try {
          ytPlayer.playVideo();
        } catch (e) {
          console.error('Error starting playback:', e);
        }
      }, 1000);
    }

    // Try to detect cast state changes through the iframe
    const iframe = ytPlayer.getIframe();
    if (iframe) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.classList.contains('ytp-cast-connected')) {
            setIsCasting(true);
          } else if (mutation.target.classList.contains('ytp-cast-button')) {
            setIsCasting(false);
          }
        });
      });

      iframe.addEventListener('load', () => {
        const castButton = iframe.contentDocument?.querySelector('.ytp-cast-button');
        if (castButton) {
          observer.observe(castButton, {
            attributes: true,
            attributeFilter: ['class']
          });
        }
      });
    }
  };

  const onPlayerStateChange = (event) => {
    // Don't play if session isn't active
    if(isCasting){
      event.target.stopVideo();
    }
    if (sessionDetails?.status !== 'ACTIVE') {
      if (event.data === 1) { // If playing
        event.target.stopVideo();
      }
      return;
    }

    if (event.data === 1) { // Video playing
      setIsPlaying(true);
      setVideoLoadError(false);
    } else if (event.data === 2) { // Video paused
      setIsPlaying(false);
    }

    if (event.data === 0 && queueItems.length > 0) { // Video ended naturally
      // Record as a legitimately completed song, then let the ad flow decide
      // whether to show a break before advancing. The actual queue advance
      // happens through onResume -> continueToNextSongRef.
      handleSongCompleted();
    }
  };

  const onPlayerError = (event) => {
    console.error('YouTube player error:', event.data);
    setVideoLoadError(true);
    setIsPlaying(false);
    // Errors are not completed songs — go to next without recording
    handleNextSong();
  };

  const fetchSessionDetails = async (userToken) => {
    if (!userToken) {
      setError('No authentication token available');
      setIsLoading(false);
      return;
    }

    try {
      const sessionResponse = await fetch(`${config.apiUrl}/sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      
      if (!sessionResponse.ok) {
        throw new Error(`HTTP error! status: ${sessionResponse.status}`);
      }
      
      const sessionData = await sessionResponse.json();
      
      setSessionDetails(sessionData);
      
      // Debug log to check IDs
      console.log('Session owner ID:', sessionData.ownerId);
      console.log('Current user ID:', auth.currentUser?.uid);
      
      // Update isSessionOwner state
      const currentUserId = auth.currentUser?.uid;
      setIsSessionOwner(sessionData.adminId === currentUserId);

      setError(null);
    } catch (error) {
      console.error('Error fetching session details:', error);
      setError('Failed to load session details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchTokenAndSession = async () => {
      try {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
          if (user) {
            const userToken = await user.getIdToken();
            setToken(userToken);
            await fetchSessionDetails(userToken);
          } else {
            // Show auth modal instead of error
            setShowAuthModal(true);
            setIsLoading(false);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error in fetchTokenAndSession:', error);
        setError('Failed to authenticate');
        setIsLoading(false);
      }
    };

    fetchTokenAndSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Update the useEffect for real-time listeners
  useEffect(() => {
    if (!sessionId || !auth.currentUser) return;

    console.log('Setting up listeners with user:', auth.currentUser.uid);

    const queueRef = ref(dbRealTime, `queues/${sessionId}`);

    // Narrow session listeners — subscribe only to fields that change dynamically.
    // Avoids re-downloading the entire session node (incl. participants map) on every participant join/leave.
    const isPlayingRef = ref(dbRealTime, `sessions/${sessionId}/isPlaying`);
    const currentVideoRef = ref(dbRealTime, `sessions/${sessionId}/currentVideo`);
    const statusRef = ref(dbRealTime, `sessions/${sessionId}/status`);

    const unsubIsPlaying = onValue(isPlayingRef, (snapshot) => {
      const val = snapshot.val();
      setIsPlaying(val || false);
      setSessionDetails(prev => prev ? { ...prev, isPlaying: val || false } : prev);
    }, (error) => {
      console.error('isPlaying listener error:', error);
      if (error.code === 'PERMISSION_DENIED') {
        setError('Access to session denied. Please check your permissions.');
      }
    });

    const unsubCurrentVideo = onValue(currentVideoRef, (snapshot) => {
      const val = snapshot.val();
      if (val) setCurrentVideo(val);
      setSessionDetails(prev => prev ? { ...prev, currentVideo: val } : prev);
    });

    const unsubStatus = onValue(statusRef, (snapshot) => {
      const val = snapshot.val();
      setSessionDetails(prev => prev ? { ...prev, status: val } : prev);
    });

    // Listen for queue updates with immediate synchronization
    onValue(queueRef, async (snapshot) => {
      const queueData = snapshot.val();
      console.log('Raw queue data from Firebase:', queueData);
      
      if (queueData) {
        try {
          // Reset the queue first
          const queueArray = [];
          
          // Convert Firebase object to array, maintaining only valid items
          Object.entries(queueData).forEach(([key, value]) => {
            if (value && value.videoId && value.songTitle) {
              queueArray.push({
                ...value,
                id: value.id || key
              });
            }
          });

          // Sort by addedAt timestamp
          queueArray.sort((a, b) => (a.addedAt || 0) - (b.addedAt || 0));

          console.log('Processed queue array:', queueArray);

          // Update queue items state with only the data from Firebase
          setQueueItems(queueArray);

          // Find currently playing song
          const currentlyPlaying = queueArray.find(item => item.isPlaying === true);
          if (currentlyPlaying) {
            console.log('Found currently playing song:', currentlyPlaying);
            setCurrentVideo(currentlyPlaying);
          }

          // Update unplayed queue items
          const unplayedSongs = queueArray.filter(item => !item.played && !item.isPlaying);
          setUnplayedQueueItems(unplayedSongs);

          // If this is the admin session and we're casting, send the updated queue to the cast device
          if (isSessionOwner && isCasting && castRef.current) {
            try {
              // Find the most recently added song (highest addedAt timestamp)
              const latestSong = queueArray.reduce((latest, current) => {
                return (!latest || (current.addedAt > latest.addedAt)) ? current : latest;
              }, null);

              if (latestSong && !latestSong.isPlaying && !latestSong.played) {
                console.log('Sending new song to cast device:', latestSong);
                await castRef.current.sendMessage("ADD_QUEUE", latestSong);
              }
            } catch (error) {
              console.error('Error sending queue update to cast device:', error);
            }
          }

        } catch (error) {
          console.error('Error processing queue data:', error);
          setError('Error processing queue data');
        }
      } else {
        // Reset all queue-related states if queue is empty
        console.log('Queue is empty, resetting states');
        setQueueItems([]);
        setUnplayedQueueItems([]);
        setCurrentVideo(null);
      }
    }, (error) => {
      console.error('Queue listener error:', error);
      if (error.code === 'PERMISSION_DENIED') {
        setError('Access to queue denied. Please check your permissions.');
      }
    });

    // Listen for participants updates
    const participantsRef = ref(dbRealTime, `sessions/${sessionId}/participants`);
    onValue(participantsRef, (snapshot) => {
      const participantsData = snapshot.val();
      if (participantsData) {
        console.log('Participants data received:', participantsData);
        const previousParticipants = Object.keys(participants);
        const currentParticipants = Object.keys(participantsData);
        
        // Check for new participants (only notify owner)
        if (isSessionOwner && previousParticipants.length > 0) {
          const newParticipant = currentParticipants.find(
            uid => !previousParticipants.includes(uid) && uid !== auth.currentUser?.uid
          );
          if (newParticipant && participantsData[newParticipant]) {
            setNewJoinNotification(participantsData[newParticipant].displayName || participantsData[newParticipant].email);
            setTimeout(() => setNewJoinNotification(null), 3000);
          }
        }
        
        setParticipants(participantsData);
      } else {
        setParticipants({});
      }
    }, (error) => {
      console.error('Participants listener error:', error);
    });

    // Cleanup listeners on unmount
    return () => {
      console.log('Cleaning up listeners');
      unsubIsPlaying();
      unsubCurrentVideo();
      unsubStatus();
      off(queueRef);
      off(participantsRef);
    };
  }, [sessionId, isSessionOwner, participants, isCasting]);

  // Auto-join session for non-owners
  useEffect(() => {
    const autoJoinSession = async () => {
      if (!sessionId || !auth.currentUser || !token || !sessionDetails) return;
      if (isSessionOwner || hasJoinedRef.current) return; // Don't join if owner or already joined
      
      try {
        console.log('Auto-joining session as participant...');
        
        // Get guest name if user is anonymous
        let displayName = null;
        if (auth.currentUser.isAnonymous) {
          displayName = localStorage.getItem(`guestName_${auth.currentUser.uid}`) || 'Guest';
        }
        
        const response = await fetch(`${config.apiUrl}/sessions/${sessionId}/join`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: displayName ? JSON.stringify({ displayName }) : undefined
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Successfully joined session:', data);
          hasJoinedRef.current = true;

          // Register server-side cleanup: Firebase automatically removes the participant
          // record when this client disconnects (tab close, network drop, etc.)
          const participantRef = ref(dbRealTime, `sessions/${sessionId}/participants/${auth.currentUser.uid}`);
          onDisconnect(participantRef).remove();
          
          // Show join confirmation for viewer
          setError(`You joined ${sessionDetails.sessionName || 'the session'}!`);
          setTimeout(() => setError(null), 3000);
        } else {
          console.error('Failed to join session:', await response.text());
        }
      } catch (error) {
        console.error('Error auto-joining session:', error);
      }
    };

    autoJoinSession();
  }, [sessionId, token, sessionDetails, isSessionOwner]);

  // Add this after the existing useEffect hooks
  useEffect(() => {
    const initializeSessionData = async () => {
      if (!sessionId || !auth.currentUser) return;

      try {
        const sessionRef = ref(dbRealTime, `sessions/${sessionId}`);
        const snapshot = await get(sessionRef);

        if (!snapshot.exists()) {
          // Initialize session data if it doesn't exist
          await set(sessionRef, {
            adminId: auth.currentUser.uid,
            sessionName: 'New Session',
            status: 'PENDING',
            isPlaying: false,
            currentVideo: null,
            createdAt: Date.now(),
            updatedAt: Date.now()
          });

          // Initialize empty queue
          const queueRef = ref(dbRealTime, `queues/${sessionId}`);
          await set(queueRef, {});
        }
      } catch (error) {
        console.error('Error initializing session data:', error);
        setError('Failed to initialize session');
      }
    };

    initializeSessionData();
  }, [sessionId]);

  // Add this after the existing useEffect hooks
  useEffect(() => {
    const syncExistingQueue = async () => {
      if (!sessionId || !auth.currentUser || !queueItems.length) return;

      try {
        const queueRef = ref(dbRealTime, `queues/${sessionId}`);
        const snapshot = await get(queueRef);

        if (!snapshot.exists()) {
          // Convert existing queue items to the proper structure
          const queueUpdates = {};
          queueItems.forEach(item => {
            const queueItem = {
              id: item.id || Date.now().toString(),
              videoId: item.videoId,
              songTitle: item.songTitle,
              userId: item.userId,
              played: item.played || false,
              isPlaying: item.isPlaying || false,
              addedAt: item.addedAt || Date.now()
            };
            queueUpdates[queueItem.id] = queueItem;
          });
          
          // Update Firebase with existing queue items
          await update(queueRef, queueUpdates);
          console.log('Synced existing queue items to Firebase');
        }
      } catch (error) {
        console.error('Error syncing existing queue:', error);
      }
    };

    syncExistingQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, queueItems.length]);

  const handleSearchSongs = async (query) => {
    if (!query.trim() || query.length < 5 || !token) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${config.searchApiUrl}/videos/search?q=${encodeURIComponent(query.toLowerCase())}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        if (response.status !== 404) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        setSearchResults([]);
        return;
      }

      const data = await response.json();
      setSearchResults(data);
      setError(null);
    } catch (error) {
      console.error('Error searching videos:', error);
      setError('Failed to search videos');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 5) {
        handleSearchSongs(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, token]);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.length < 5) {
      setSearchResults([]);
    }
  };

  const handleSelectSong = async (video) => {
    console.log('handleSelectSong called with video:', video);
    
    if (!sessionId || !auth.currentUser) {
      console.error('Missing sessionId or auth:', { sessionId, user: auth.currentUser?.uid });
      setError('Please sign in to add songs');
      return;
    }

    try {
      console.log('Starting to add song to queue...');
      
      // Get display name for queue item
      let displayName = 'Anonymous';
      if (auth.currentUser.isAnonymous) {
        // For anonymous users, get guest name from localStorage
        displayName = localStorage.getItem(`guestName_${auth.currentUser.uid}`) || 'Anonymous';
      } else if (auth.currentUser.email) {
        displayName = auth.currentUser.email;
      }
      
      const newQueueItem = {
        id: Date.now().toString(),
        videoId: video.id,
        songTitle: video.title,
        userId: auth.currentUser.uid,
        userEmail: displayName, // Now uses guest name for anonymous users
        played: false,
        isPlaying: false,
        addedAt: Date.now()
      };

      console.log('New queue item:', newQueueItem);
      console.log(`Adding to Firebase path: queues/${sessionId}/${newQueueItem.id}`);

      // Add song to queue using Realtime Database
      const queueRef = ref(dbRealTime, `queues/${sessionId}/${newQueueItem.id}`);
      console.log('Queue ref created:', queueRef);
      console.log('dbRealTime is:', dbRealTime);
      
      console.log('About to call set()...');
      const setPromise = set(queueRef, newQueueItem);
      console.log('set() called, promise returned:', setPromise);
      
      await Promise.race([
        setPromise.then(() => console.log('set() completed successfully')),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('set() timed out after 5 seconds')), 5000)
        )
      ]);
      
      console.log('Successfully added to Firebase');

      // Update local state
      setAddedSongs(prev => new Set(prev).add(video.id));
      
      // Update casting if needed
      if (isCasting && castRef.current) {
        try {
          await castRef.current.sendMessage("ADD_QUEUE", newQueueItem);
        } catch (castError) {
          console.error('Error sending cast message:', castError);
        }
      }

      setError(null);
    } catch (error) {
      console.error('Error adding song to queue:', error);
      console.error('Full error object:', {
        code: error.code,
        message: error.message,
        name: error.name
      });
      
      // Show detailed error message to user
      const errorMsg = error.code === 'PERMISSION_DENIED' 
        ? 'Permission denied - check Firebase rules'
        : error.message || 'Failed to add song to queue';
      
      setError('Failed to add song to queue: ' + errorMsg);
      
      setAddedSongs(prev => {
        const newSet = new Set(prev);
        newSet.delete(video.id);
        return newSet;
      });
    }
  };

  const handlePlayPause = async () => {
    if (!player || sessionDetails?.status !== 'ACTIVE') return;
    
    const newPlayingState = !isPlaying;
    
    // Update Firebase
    const sessionRef = ref(dbRealTime, `sessions/${sessionId}`);
    await update(sessionRef, {
      isPlaying: newPlayingState
    });

    if (newPlayingState) {
      handlePlay();
    } else {
      handlePause();
    }
  };

  const handleStop = async () => {    
   
    if (isCasting && castRef.current) {
      try {
        await castRef.current.sendMessage("STOP_VIDEO");
        setIsPlaying(false); // Usually next video starts playing
      } catch (error) {
        console.error('Failed to send next command:', error);
      }
    } else {
      // Load the video
      if (player) {
        player.stopVideo();
        setIsPlaying(false);
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const loadVideo = async (videoToLoad, startTime = 0) => {
    if (!videoToLoad || !videoToLoad.videoId) {
      console.warn('Cannot load video: missing video data');
      return;
    }

    if (!player) {
      console.warn('Player not initialized');
      setError('Video player not ready. Please wait...');
      return;
    }

    // Helper to check if iframe is ready
    function isIframeReady(playerInstance) {
      const iframe = playerInstance && typeof playerInstance.getIframe === 'function' ? playerInstance.getIframe() : null;
      return iframe && iframe.src && typeof iframe.src === 'string' && iframe.src.startsWith('https://');
    }

    try {
      console.log('Loading video:', videoToLoad.videoId, 'at time:', startTime);
      
      // Check if iframe is ready before calling cueVideoById
      if (!isIframeReady(player)) {
        console.log('Iframe not ready in loadVideo, waiting...');
        let attempts = 0;
        const tryLoad = () => {
          attempts++;
          if (!isIframeReady(player)) {
            if (attempts < 10) {
              setTimeout(tryLoad, 300);
            } else {
              console.error('Iframe not ready after multiple attempts in loadVideo');
              setVideoLoadError(true);
              setError('Video player not ready. Please try again.');
            }
            return;
          }
          
          // Iframe is ready, load the video
          try {
            player.cueVideoById({
              videoId: videoToLoad.videoId,
              startSeconds: startTime
            });
            
            // Start playing after a short delay
            setTimeout(() => {
              if (player && player.playVideo) {
                player.playVideo();
              }
            }, 1000);
          } catch (err) {
            console.error('Error calling cueVideoById:', err);
            setVideoLoadError(true);
            setError('Failed to load video. Please try again.');
          }
        };
        setTimeout(tryLoad, 300);
        return;
      }
      
      // Iframe is ready, proceed with loading
      player.cueVideoById({
        videoId: videoToLoad.videoId,
        startSeconds: startTime
      });

      // Start playing after a short delay
      setTimeout(() => {
        if (player && player.playVideo) {
          player.playVideo();
        }
      }, 1000);

    } catch (error) {
      console.error('Error in loadVideo:', error);
      setVideoLoadError(true);
      setError('Failed to load video. Please try again.');
    }
  };

  const handleNextSong = async () => {
    if (!sessionId || queueItems.length === 0 || sessionDetails?.status !== 'ACTIVE') {
      console.log('Early return conditions not met:', { sessionId, queueLength: queueItems.length, status: sessionDetails?.status });
      return;
    }

    // Prevent multiple simultaneous song changes
    if (isChangingSong) {
      console.log('Song change already in progress, ignoring click');
      return;
    }

    try {
      setIsChangingSong(true);
      setVideoLoadError(false);
      
      // Find the next unplayed song in the queue
      const nextVideo = queueItems.find(item => !item.played && (!currentVideo || item.id !== currentVideo.id));
      
      if (!nextVideo || !nextVideo.videoId) {
        console.log('No valid next video in queue:', nextVideo);
        setError('No valid next video in queue.');
        setVideoLoadError(true);
        handleStop();
        return;
      }
      
      console.log('Found next video:', nextVideo);

      const newSongsPlayed = (sessionDetails?.songsPlayedCount || 0) + 1;

      // Prepare updates for Firebase
      const updates = {};
      
      // Mark current video as played if it exists
      if (currentVideo && currentVideo.id) {
        updates[`queues/${sessionId}/${currentVideo.id}`] = {
          ...currentVideo,
          played: true,
          isPlaying: false,
          updatedAt: Date.now()
        };
      }

      // Mark next video as playing
      updates[`queues/${sessionId}/${nextVideo.id}`] = {
        ...nextVideo,
        isPlaying: true,
        played: false,
        updatedAt: Date.now()
      };

      // Update session state (increment song counter)
      updates[`sessions/${sessionId}`] = {
        ...(sessionDetails || {}),
        currentVideo: nextVideo,
        isPlaying: true,
        songsPlayedCount: newSongsPlayed,
        adRequired: false,
        updatedAt: Date.now()
      };

      // Update Firebase first
      console.log('Updating Firebase with:', updates);
      const rootRef = ref(dbRealTime);
      await update(rootRef, updates);

      // Update local state
      setCurrentVideo(nextVideo);
      setIsPlaying(true);

      // Handle casting scenario
      if (isCasting && castRef.current) {
        try {
          await castRef.current.sendMessage("NEXT_VIDEO", nextVideo);
          setIsChangingSong(false);
          return; // Exit early for casting scenario
        } catch (error) {
          console.error('Failed to send cast command:', error);
        }
      }

      // For non-casting scenario, the YouTube component will automatically
      // reload the video when currentVideo.videoId changes
      // We just need to wait a bit before allowing the next song change
      setTimeout(() => setIsChangingSong(false), 2000);

    } catch (error) {
      console.error('Error in handleNextSong:', error);
      setError('Failed to change to next song: ' + error.message);
      setVideoLoadError(true);
      setIsChangingSong(false);
    }
  };

  // Keep the ref current so useAdBreakFlow.onResume always calls the latest version
  continueToNextSongRef.current = handleNextSong;

  // Authentication handlers
  const handleGuestJoin = async () => {
    if (!guestName.trim()) {
      setError('Please enter your name');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!authEmail.trim()) {
      setError('Please enter your email or phone number');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setAuthLoading(true);
    try {
      // Sign in anonymously
      const { signInAnonymously } = await import('firebase/auth');
      const userCredential = await signInAnonymously(auth);
      
      // Store guest name in local storage for display
      localStorage.setItem(`guestName_${userCredential.user.uid}`, guestName);
      
      // Save guest info to Firestore for marketing (now required)
      try {
        const guestRef = doc(db, 'guests', userCredential.user.uid);
        await setDoc(guestRef, {
          name: guestName,
          contact: authEmail, // Can be email or phone
          joinedAt: serverTimestamp(),
          sessionId: sessionId,
          uid: userCredential.user.uid
        });
        console.log('Guest contact info saved for marketing');
      } catch (err) {
        console.error('Error saving guest info:', err);
        // Don't block the join if this fails
      }
      
      // Get token and fetch session
      const userToken = await userCredential.user.getIdToken();
      setToken(userToken);
      await fetchSessionDetails(userToken);
      
      setShowAuthModal(false);
      setToast(`Welcome, ${guestName}! 🎉`);
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error('Error with guest join:', error);
      setError('Failed to join as guest. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailSignIn = async () => {
    if (!authEmail.trim() || !authPassword.trim()) {
      setError('Please enter email and password');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setAuthLoading(true);
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const userCredential = await signInWithEmailAndPassword(auth, authEmail, authPassword);
      
      const userToken = await userCredential.user.getIdToken();
      setToken(userToken);
      await fetchSessionDetails(userToken);
      
      setShowAuthModal(false);
      setToast('Signed in successfully! 🎉');
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error('Error signing in:', error);
      setError('Failed to sign in. Check your credentials.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailSignUp = async () => {
    if (!authEmail.trim() || !authPassword.trim()) {
      setError('Please enter email and password');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (authPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setAuthLoading(true);
    try {
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      const userCredential = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
      
      const userToken = await userCredential.user.getIdToken();
      setToken(userToken);
      await fetchSessionDetails(userToken);
      
      setShowAuthModal(false);
      setToast('Account created successfully! Welcome! 🎉');
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error('Error signing up:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('Email already in use. Try signing in instead.');
      } else {
        setError('Failed to create account. Please try again.');
      }
      setTimeout(() => setError(null), 3000);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleShareSession = () => {
    setShowShareModal(true);
    setIsSessionShared(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleCopyLink = async () => {
    try {
      const sessionUrl = window.location.href;
      await navigator.clipboard.writeText(sessionUrl);
      // Show toast success message
      setToast('Link copied to clipboard!');
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error('Error copying link:', error);
      setError('Failed to copy link');
    }
  };

  const handleAddToQueue = () => setShowAddSongModal(true);

  const handleStartSession = async () => {
    if (!token || !sessionId) return;

    try {
      // Check queue in Firebase Realtime Database
      const queueRef = ref(dbRealTime, `queues/${sessionId}`);
      const queueSnapshot = await get(queueRef);
      
      if (!queueSnapshot.exists() || Object.keys(queueSnapshot.val()).length === 0) {
        setError('Cannot start session with empty queue');
        return;
      }

      const endpoint = sessionDetails?.status === 'ACTIVE' 
        ? 'resume'
        : 'start';

      // First update the Realtime Database
      const sessionRef = ref(dbRealTime, `sessions/${sessionId}`);
      await update(sessionRef, {
        adminId: auth.currentUser.uid,
        sessionName: sessionDetails?.sessionName || 'New Session',
        status: 'ACTIVE',
        isPlaying: false,
        currentVideo: null,
        createdAt: Date.now(),
        updatedAt: Date.now()
      });

      // Make backend call
      const response = await fetch(`${config.apiUrl}/sessions/${sessionId}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        let backendMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          backendMessage = errorData?.error || errorData?.message || backendMessage;
        } catch (e) {
          // Keep fallback message when response body is not JSON.
        }
        throw new Error(backendMessage);
      }

      // Update local state
      const newStatus = 'ACTIVE';
      setSessionDetails(prev => ({ ...prev, status: newStatus }));

      if (isCasting && castRef.current) {
        castRef.current.sendMessage("START", queueItems);
      }

      // If there's a current video, start playing it
      if (player && currentVideo) {
        player.loadVideoById(currentVideo.videoId);
        player.playVideo();
        setIsPlaying(true);
      } else if (player && queueItems.length > 0) {
        // Find the first valid queue item
        const firstValidVideo = queueItems.find(item => item && item.videoId);
        if (firstValidVideo) {
          setCurrentVideo(firstValidVideo);
          player.loadVideoById(firstValidVideo.videoId);
          player.playVideo();
          setIsPlaying(true);

          // Update queue items locally and in Firebase
          const updatedQueueItems = queueItems.map((item) => {
            if (!item || !item.videoId) return null;
            return {
              ...item,
              id: item.id || Date.now().toString(),
              userId: item.userId || auth.currentUser?.uid,
              isPlaying: item.videoId === firstValidVideo.videoId,
              played: false,
              addedAt: item.addedAt || Date.now()
            };
          }).filter(Boolean); // Remove any null items

          // Update Firebase with the updated queue items
          const queueUpdates = {};
          updatedQueueItems.forEach(item => {
            queueUpdates[item.id] = item;
          });
          await update(queueRef, queueUpdates);

          setQueueItems(updatedQueueItems);
          setUnplayedQueueItems(updatedQueueItems.filter(item => !item.played));
        }
      }

    } catch (error) {
      console.error('Error starting/resuming session:', error);
      setError(error.message || 'Failed to start/resume session');
    }
  };

  const getStartButtonText = () => {
    if (!isSessionOwner) return null;
    if (sessionDetails?.status === 'ACTIVE') return 'Resume Session';
    return 'Start Session';
  };

  // Reset added songs when modal is closed
  const handleCloseModal = () => {
    setShowAddSongModal(false);
    setAddedSongs(new Set());
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle cast status changes
  const handleCastStatusChange = async (isConnected) => {
    console.log('Cast status changed:', isConnected);
    setIsCasting(isConnected);

    // Reset playing state when cast connection changes
    setIsPlaying(false);
    if(isConnected){
      
      const queueResponse = await fetch(`${config.apiUrl}/sessions/${sessionId}/queue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (queueResponse.ok) {
        const queueData = await queueResponse.json();
        setQueueItems(queueData);
        //execute the command to get the queue
        castRef.current.sendMessage("GET_QUEUE", queueData);
        if (player) {        
          console.log('Casting true so Pausing video');
          player.pauseVideo();
        }else{
          console.log('Player is not ready');
        }
      }
    }
  };

  const handlePlay = async () => {
    if (isCasting && castRef.current) {
      try {
        await castRef.current.sendMessage("PLAY_VIDEO");
        setIsPlaying(true);
      } catch (error) {
        console.error('Failed to send play command:', error);
      }
    } else {
      // Your existing play logic
      if (player) {
        player.playVideo();
        setIsPlaying(true);
      }
    }
  };

  const handlePause = async () => {
    if (isCasting && castRef.current) {
      try {
        await castRef.current.sendMessage("PAUSE_VIDEO");
        setIsPlaying(false);
      } catch (error) {
        console.error('Failed to send pause command:', error);
      }
    } else {
      // Your existing pause logic
      if (player) {
        player.pauseVideo();
        setIsPlaying(false);
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleNext = async () => {
    if (isCasting && castRef.current) {
      try {
        await castRef.current.sendMessage("NEXT_VIDEO");
        setIsPlaying(true); // Usually next video starts playing
      } catch (error) {
        console.error('Failed to send next command:', error);
      }
    } else {
      // Your existing next logic
      handleSongCompleted();
    }
  };

  if (isLoading) {
    return (
      <div className="play-session-page">
        <NavBar />
        <div className="play-session">
          <div className="loading-container">
            <p>Loading session...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="play-session-page">
        <NavBar />
        <div className="play-session">
          <div className="error-container">
            <p className="error-message">{error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="play-session-page">
      <NavBar />
      <div className="play-session">
        
        {/* Join Notification Toast */}
        {newJoinNotification && isSessionOwner && (
          <div className="join-notification-toast">
            <i className="fas fa-user-plus"></i>
            <span>{newJoinNotification} joined the session</span>
          </div>
        )}
        
        {/* Success Toast Notification */}
        {toast && (
          <div className="success-toast">
            <i className="fas fa-check-circle"></i>
            <span>{toast}</span>
          </div>
        )}

        {sessionDetails && (
          <div className="session-header">
            <div className="session-info-row">
              <div className="now-playing-section">
                {currentVideo ? (
                  <>
                    <div className="now-playing-label">
                      <i className="fas fa-music"></i>
                      <span>Now Playing</span>
                    </div>
                    <div className="now-playing-details">
                      <h2>{currentVideo.songTitle}</h2>
                    </div>
                  </>
                ) : (
                  <div className="no-song-playing">
                    <i className="fas fa-music"></i>
                    <span>No song playing</span>
                  </div>
                )}
              </div>
              <div className="session-details">
                <h1>{sessionDetails.sessionName}</h1>
                <div className="session-status-container">
                  {Object.keys(participants).length > 0 && (
                    <button 
                      className="participants-badge" 
                      onClick={() => setShowParticipantsModal(true)}
                      title="View participants"
                    >
                      <i className="fas fa-users"></i>
                      <span>{Object.keys(participants).length} watching</span>
                    </button>
                  )}
                  {isSessionShared && (
                    <span className="sharing-indicator">
                      <i className="fas fa-users"></i>
                      Shared
                    </span>
                  )}
                  <span className={`session-status ${sessionDetails?.status?.toLowerCase() ?? 'unknown'}`}>
                    {sessionDetails.status ?? 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
  
        <div className="video-player-section">
          {videoLoadError && (
            <div className="video-error-overlay">
              <i className="fas fa-exclamation-circle"></i>
              <p>This video cannot be played. Please try another song.</p>
            </div>
          )}
          {isSessionOwner ? (
            // Show YouTube player for session owner
            currentVideo?.videoId ? (
              <YouTube
                videoId={currentVideo.videoId}
                opts={opts}
                onReady={onPlayerReady}
                onStateChange={onPlayerStateChange}
                onError={onPlayerError}
                className={playerClassName}
              />
            ) : (
              <div className="no-video-placeholder">
                <i className="fas fa-video-slash"></i>
                <p>No video loaded. Add songs to the queue and start the session.</p>
              </div>
            )
          ) : (
            // Show queue-only view for non-owners
            <div className="viewer-queue-display">
              <div className="viewer-header">
                <i className="fas fa-music"></i>
                <h3>Song Queue</h3>
                <p>Follow along with what's playing and coming up next!</p>
              </div>
            </div>
          )}
          {isCasting && (
            <div className="casting-overlay">
              <i className="fas fa-cast"></i>
              <span>Casting to device...</span>
            </div>
          )}
          {!isSessionOwner && (
            <div className="viewer-notice">
              <i className="fas fa-info-circle"></i>
              <span>You're viewing this session. Only the session owner can control playback.</span>
            </div>
          )}
        </div>
  
        <div className="controls-and-qr-section">
          <div className="controls-section">
            <Cast ref={castRef} sessionId={sessionId} onCastStatusChange={handleCastStatusChange} />
            
            <div className="primary-controls">
              {isSessionOwner ? (
                <>
                  <button 
                    className={`control-btn primary ${isPlaying ? 'playing' : ''}`} 
                    onClick={handlePlayPause}
                    disabled={sessionDetails?.status !== 'ACTIVE'}
                  >
                    <i className={`fas fa-${isPlaying ? 'pause' : 'play'}`}></i>
                    {isPlaying ? 'Pause' : 'Play'}
                  </button>
                  <button 
                    className="control-btn primary" 
                    onClick={handleStop}
                    disabled={sessionDetails?.status !== 'ACTIVE'}
                  >
                    <i className="fas fa-stop"></i>
                    Stop
                  </button>
                  <button 
                    className="control-btn primary" 
                    onClick={handleSongCompleted}
                    disabled={sessionDetails?.status !== 'ACTIVE' || queueItems.length === 0 || isChangingSong}
                  >
                    <i className="fas fa-forward"></i>
                    {isChangingSong ? 'Loading...' : 'Next Song'}
                  </button>
                </>
              ) : (
                <>
                  <div className={`status-indicator ${isPlaying ? 'playing' : 'paused'}`}>
                    <i className={`fas fa-${isPlaying ? 'pause' : 'play'}`}></i>
                    <span>{isPlaying ? 'Playing' : 'Paused'}</span>
                  </div>
                  {isCasting && (
                    <div className="status-indicator casting">
                      <i className="fas fa-cast"></i>
                      <span>Casting</span>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="secondary-controls">
              <button className="control-btn secondary" onClick={handleAddToQueue}>
                <i className="fas fa-plus"></i>
                Add to Queue
              </button>
              {isSessionOwner && (
                <>
                  <button 
                    className="control-btn secondary" 
                    onClick={handleStartSession}
                    disabled={queueItems.length === 0}
                  >
                    <i className="fas fa-play-circle"></i>
                    {getStartButtonText()}
                  </button>
                  <button className="control-btn secondary" onClick={handleShareSession}>
                    <i className="fas fa-share"></i>
                    Share Session
                  </button>
                </>
              )}
            </div>
          </div>

          <style jsx>{`
            .status-indicator {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 8px 16px;
              border-radius: 4px;
              background: rgba(0, 0, 0, 0.1);
              color: #666;
            }
            .status-indicator.playing {
              color: #4CAF50;
            }
            .status-indicator.paused {
              color: #FFA000;
            }
            .status-indicator.casting {
              color: #2196F3;
            }
            .viewer-status {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 2rem;
              background: rgba(0, 0, 0, 0.05);
              border-radius: 8px;
              text-align: center;
            }
            .status-message {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 1.2rem;
              margin-bottom: 1rem;
            }
            .status-message i {
              color: #4CAF50;
            }
            .current-song {
              color: #666;
              margin: 0;
            }
          `}</style>

          {isSessionShared && (
            <div className="qr-code-section">
              <div className="qr-code-wrapper">
                <QRCodeSVG 
                  value={window.location.href}
                  size={150}
                  level="H"
                  includeMargin={true}
                />
                <p className="qr-code-label">Scan to join</p>
              </div>
            </div>
          )}
        </div>
  
        {/* Persistent inline ad for free users — always visible as part of the
            page layout, surrounded by publisher content (player, controls, queue).
            Never triggered by events; complies with AdSense programme policies. */}
        {isFreeUser && <AdsterraBanner />}

        {sessionDetails && (
          <div className="queue-section">
            <h3>Up Next ({unplayedQueueItems.length})</h3>
            {unplayedQueueItems.length === 0 && (
              <div className="queue-empty-message">
                <p>No more songs in the queue!</p>
                <p>Add more songs to keep the party going 🎉</p>
              </div>
            )}
            <div className="queue-list">
              {unplayedQueueItems.length > 0 ? (
                unplayedQueueItems.map((item, index) => {
                  const isCurrentVideo = currentVideo && item.id === currentVideo.id;
                  const participant = participants[item.userId];
                  
                  // Get display name: Use participant data from Realtime DB (synced to all users)
                  // Priority: participant displayName > userEmail from queue item > 'Anonymous'
                  let singerName = 'Anonymous';
                  if (participant?.displayName) {
                    singerName = participant.displayName;
                  } else if (participant?.email && participant.email !== 'Anonymous') {
                    singerName = participant.email.split('@')[0];
                  } else if (item.userEmail && item.userEmail !== 'Anonymous') {
                    singerName = item.userEmail;
                  }
                  
                  return (
                    <div 
                      key={`${item.id}-${index}`} 
                      className={`queue-item ${isCurrentVideo ? 'now-playing-item' : ''}`}
                    >
                      <span className="queue-number">{index + 1}</span>
                      <div className="queue-item-details">
                        <h4>
                          {item.songTitle}
                          {isCurrentVideo && (
                            <span className="now-singing-badge">
                              <i className="fas fa-microphone"></i>
                              Now Singing
                            </span>
                          )}
                        </h4>
                        <p className="queue-singer">
                          <i className="fas fa-user-circle"></i>
                          {singerName}
                          {isCurrentVideo && "'s turn"}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <button 
                  className="add-songs-btn" 
                  onClick={handleAddToQueue}
                >
                  <i className="fas fa-plus"></i>
                  Add Songs to Queue
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {showAddSongModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add Song to Queue</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="search-container">
              <input
                type="text"
                placeholder="Search for songs..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                className="search-input"
              />
            </div>

            <div className="search-results">
              {isSearching ? (
                <div className="loading">Searching...</div>
              ) : (
                <>
                  <div className="results-list">
                    {searchResults.map((song) => (
                      <div key={song.id} className="song-result">
                        <img 
                          src={song.thumbnailUrl || 'default-thumbnail.png'}
                          alt={song.title} 
                          className="song-thumbnail" 
                          onError={(e) => {
                            e.target.src = 'default-thumbnail.png';
                          }}
                        />
                        <div className="song-info">
                          <h3>{song.title}</h3>
                          <p>{song.artist}</p>
                        </div>
                        {addedSongs.has(song.id) ? (
                          <div className="added-indicator">
                            <i className="fas fa-check"></i>
                            Added
                          </div>
                        ) : (
                          <button 
                            className="add-song-btn"
                            onClick={() => handleSelectSong(song)}
                          >
                            <i className="fas fa-plus"></i>
                            Add
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer">
                    <button 
                      className="close-modal-btn" 
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal for Unauthenticated Users */}
      {showAuthModal && (
        <div className="modal-overlay auth-modal-overlay">
          <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal-header">
              <h2>Join Karaoke Session</h2>
              <p className="auth-subtitle">Quick and easy - choose how you'd like to join!</p>
            </div>
            
            <div className="auth-tabs">
              <button 
                className={`auth-tab ${authMode === 'guest' ? 'active' : ''}`}
                onClick={() => setAuthMode('guest')}
              >
                <i className="fas fa-user"></i>
                Join as Guest
              </button>
              <button 
                className={`auth-tab ${authMode === 'signin' ? 'active' : ''}`}
                onClick={() => setAuthMode('signin')}
              >
                <i className="fas fa-sign-in-alt"></i>
                Sign In
              </button>
              <button 
                className={`auth-tab ${authMode === 'signup' ? 'active' : ''}`}
                onClick={() => setAuthMode('signup')}
              >
                <i className="fas fa-user-plus"></i>
                Sign Up
              </button>
            </div>

            <div className="auth-content">
              {authMode === 'guest' && (
                <div className="auth-form">
                  <div className="auth-info">
                    <i className="fas fa-info-circle"></i>
                    <p>Join instantly without creating an account. Perfect for quick karaoke sessions!</p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="guestName">Your Name</label>
                    <input
                      id="guestName"
                      type="text"
                      placeholder="Enter your name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      disabled={authLoading}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="authEmail">Email or Phone Number *</label>
                    <input
                      id="authEmail"
                      type="text"
                      placeholder="your@email.com or +639123456789"
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleGuestJoin()}
                      disabled={authLoading}
                      required
                    />
                    <small className="form-hint">We'll send you updates and special offers!</small>
                  </div>
                  <button 
                    className="auth-submit-btn guest-btn"
                    onClick={handleGuestJoin}
                    disabled={authLoading || !guestName.trim() || !authEmail.trim()}
                  >
                    {authLoading ? (
                      <><i className="fas fa-spinner fa-spin"></i> Joining...</>
                    ) : (
                      <><i className="fas fa-rocket"></i> Join as {guestName || 'Guest'}</>
                    )}
                  </button>
                  <p className="auth-note">
                    <i className="fas fa-lightbulb"></i>
                    Tip: Sign up to save your favorite songs and track your karaoke history!
                  </p>
                </div>
              )}

              {authMode === 'signin' && (
                <div className="auth-form">
                  <div className="form-group">
                    <label htmlFor="signinEmail">Email</label>
                    <input
                      id="signinEmail"
                      type="email"
                      placeholder="your@email.com"
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      disabled={authLoading}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="signinPassword">Password</label>
                    <input
                      id="signinPassword"
                      type="password"
                      placeholder="••••••••"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleEmailSignIn()}
                      disabled={authLoading}
                    />
                  </div>
                  <button 
                    className="auth-submit-btn signin-btn"
                    onClick={handleEmailSignIn}
                    disabled={authLoading || !authEmail.trim() || !authPassword.trim()}
                  >
                    {authLoading ? (
                      <><i className="fas fa-spinner fa-spin"></i> Signing In...</>
                    ) : (
                      <><i className="fas fa-sign-in-alt"></i> Sign In</>
                    )}
                  </button>
                </div>
              )}

              {authMode === 'signup' && (
                <div className="auth-form">
                  <div className="form-group">
                    <label htmlFor="signupEmail">Email</label>
                    <input
                      id="signupEmail"
                      type="email"
                      placeholder="your@email.com"
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      disabled={authLoading}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="signupPassword">Password</label>
                    <input
                      id="signupPassword"
                      type="password"
                      placeholder="At least 6 characters"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleEmailSignUp()}
                      disabled={authLoading}
                    />
                  </div>
                  <button 
                    className="auth-submit-btn signup-btn"
                    onClick={handleEmailSignUp}
                    disabled={authLoading || !authEmail.trim() || !authPassword.trim()}
                  >
                    {authLoading ? (
                      <><i className="fas fa-spinner fa-spin"></i> Creating Account...</>
                    ) : (
                      <><i className="fas fa-user-plus"></i> Create Account</>
                    )}
                  </button>
                  <p className="auth-note">
                    <i className="fas fa-shield-alt"></i>
                    Your account lets you create sessions, save favorites, and more!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipantsModal && (
        <div className="modal-overlay" onClick={() => setShowParticipantsModal(false)}>
          <div className="modal-content participants-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Participants ({Object.keys(participants).length})</h2>
              <button className="close-btn" onClick={() => setShowParticipantsModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="participants-list">
              {Object.entries(participants).map(([uid, participant]) => (
                <div key={uid} className="participant-item">
                  <div className="participant-avatar">
                    {uid === sessionDetails?.adminId ? (
                      <i className="fas fa-crown" title="Session Owner"></i>
                    ) : (
                      <i className="fas fa-user"></i>
                    )}
                  </div>
                  <div className="participant-info">
                    <div className="participant-name">
                      {participant.displayName || participant.email}
                      {uid === sessionDetails?.adminId && <span className="owner-badge">Owner</span>}
                      {uid === auth.currentUser?.uid && <span className="you-badge">You</span>}
                    </div>
                    <div className="participant-email">{participant.email}</div>
                    <div className="participant-joined">
                      Joined {new Date(participant.joinedAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="modal-footer">
              <button 
                className="close-modal-btn" 
                onClick={() => setShowParticipantsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="modal-overlay">
          <div className="modal-content share-modal">
            <div className="modal-header">
              <h2>Share Session</h2>
              <button className="close-btn" onClick={handleCloseShareModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="share-content">
              <div className="qr-code-container">
                <QRCodeSVG 
                  value={window.location.href}
                  size={200}
                  level="H"
                  includeMargin={true}
                  imageSettings={{
                    src: "/karaoke-icon.png",
                    x: undefined,
                    y: undefined,
                    height: 40,
                    width: 40,
                    excavate: true,
                  }}
                />
              </div>
              <p className="share-instructions">
                Scan this QR code to join the session
              </p>
              <div className="share-link-container">
                <input 
                  type="text" 
                  value={window.location.href} 
                  readOnly 
                  className="share-link-input"
                />
                <button 
                  className="copy-link-btn"
                  onClick={handleCopyLink}
                >
                  <i className="fas fa-copy"></i>
                  Copy
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="close-modal-btn" 
                onClick={handleCloseShareModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad break modal — shown to free users after every 3 legitimately completed songs */}
      <AdBreakModal
        open={isAdBreakOpen}
        onContinue={handleContinueAdBreak}
        onMaybeLater={handleMaybeLater}
      />
    </div>
  );
};

export default PlaySession;
